import jwtDecode from 'jwt-decode';
import React, {
    createContext,
    useCallback,
    useContext,
    useState,
} from 'react';

import api from '../services/api';

interface User {
    sub: string;
    avatar_url: string;
    name: string;
    email: string;
    permissions: any;
    typeUser: 'admin' | 'user'; // 'admin' | 'user'
    companyId: string;
    companyIds: string[];
}
interface AuthState {
    token: string;
    user: User;
}
interface SignInCredentials {
    email: string;
    password: string;
}
interface AuthContextData {
    user: User;
    signIn(credentials: SignInCredentials): Promise<void>;
    signOut(): void;
    updateUser(user: User): void;
}

type Props = { children: React.ReactNode };

export const AuthContext = createContext<AuthContextData>(
    {} as AuthContextData,
);

export const AuthProvider: React.FC<Props> = ({ children }) => {
    const [data, setData] = useState<AuthState>(() => {
        const token = localStorage.getItem('@GESSIS:token');
        if (token) {
            const user: User = jwtDecode(token);
            if (user) {
                api.defaults.headers.common[
                    'Authorization'
                ] = `Bearer ${token}`;
                api.defaults.headers.common['Cache-Control'] = 'no-cache';
                return { token, user: user };
            }
        }
        return {} as AuthState;
    });

    const signIn = async ({ email, password }: SignInCredentials) => {
        const response = await api.post('auth/login', {
            email,
            password,
        });
        const { token, refreshToken } = response.data;
        const user: User = jwtDecode(token);
        localStorage.setItem('@GESSIS:token', token);
        localStorage.setItem('@GESSIS:refreshToken', refreshToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setData({ token, user });
    };

    const signOut = useCallback(() => {
        localStorage.removeItem('@GESSIS:token');
        localStorage.removeItem('@GESSIS:refreshToken');
        localStorage.removeItem('@GESSIS:openCollapsePermissions');
        localStorage.removeItem('@GESSIS:openCollapseInvoicing');
        localStorage.removeItem('@GESSIS:configuration');
        localStorage.removeItem('@GESSIS:nextAudience');
        localStorage.removeItem('@GESSIS:nextCustomerService');
        localStorage.removeItem('@GESSIS:nextExpertise');
        localStorage.removeItem('@GESSIS:nextLegalProcess');
        localStorage.removeItem('@GESSIS:nextTask');
        setData({} as AuthState);
    }, []);

    const updateUser = useCallback(
        (user: User) => {
            localStorage.setItem('@GESSIS:user', JSON.stringify(user));
            setData({
                token: data.token,
                user,
            });
        },
        [setData, data.token],
    );

    return (
        <AuthContext.Provider
            value={{
                user: data.user,
                signIn,
                signOut,
                updateUser,
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export function useAuth(): AuthContextData {
    const context = useContext(AuthContext);

    return context;
}
